import { useState, type FormEvent } from "react";
import { contactEmail } from "@/mocks/site";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_URL = "https://readdy.ai/api/form/daim6b3p14a1h3mr76q0";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    payload.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试，或直接发送邮件联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试，或直接发送邮件联系我们。");
    }
  };

  return (
    <section
      id="subscribe"
      aria-labelledby="subscribe-heading"
      className="w-full scroll-mt-20 bg-background-50"
    >
      <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-16">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-secondary-50">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-6 lg:border-r lg:border-background-300">
              <div className="p-7 md:p-10">
                <p className="label-caps text-[11px] text-primary-600">Newsletter</p>
                <h2
                  id="subscribe-heading"
                  className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl"
                >
                  每周收到一份事件复盘清单
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  我们每周整理当周完成的事件解读，只发一篇邮件，不发广告，不做推销。你也可以顺便告诉我们最想看哪个方向的事件，编辑部会优先安排。
                </p>
                <p className="mt-6 text-sm text-foreground-600">
                  也可以直接写信给我们：
                  <a
                    href={`mailto:${contactEmail}`}
                    className="ml-1 font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    {contactEmail}
                  </a>
                </p>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="border-t border-background-300 p-7 md:p-10 lg:border-t-0">
                <form
                  id="subscribe-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div>
                    <label
                      htmlFor="subscribe-email"
                      className="mb-2 block text-xs font-medium text-foreground-700"
                    >
                      邮箱地址
                    </label>
                    <input
                      id="subscribe-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subscribe-topic"
                      className="mb-2 block text-xs font-medium text-foreground-700"
                    >
                      最关注的解读方向
                    </label>
                    <select
                      id="subscribe-topic"
                      name="topic"
                      defaultValue="娱乐观察"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="娱乐观察">娱乐观察</option>
                      <option value="商业风云">商业风云</option>
                      <option value="网络热点">网络热点</option>
                      <option value="社会话题">社会话题</option>
                      <option value="科技前沿">科技前沿</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="subscribe-note"
                      className="mb-2 block text-xs font-medium text-foreground-700"
                    >
                      想看的线索（选填，500 字以内）
                    </label>
                    <textarea
                      id="subscribe-note"
                      name="note"
                      maxLength={500}
                      rows={3}
                      placeholder="例如：希望复盘某个最近讨论很多的事件"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-extra-field"
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                  >
                    {status === "submitting" ? "提交中…" : "订阅并提交线索"}
                  </button>

                  {status === "success" ? (
                    <p
                      role="status"
                      className="flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-3 text-sm text-accent-900"
                    >
                      <i className="ri-check-line" aria-hidden="true" />
                      订阅已提交，感谢支持。我们会尽快与您同步最新解读。
                    </p>
                  ) : null}

                  {status === "error" ? (
                    <p
                      role="alert"
                      className="flex items-center gap-2 rounded-md bg-primary-100 px-3.5 py-3 text-sm text-primary-800"
                    >
                      <i className="ri-error-warning-line" aria-hidden="true" />
                      {formError}
                    </p>
                  ) : null}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}